import { type FC, useMemo, useRef, useState } from "react";
import { match } from "ts-pattern";

import type { DocumentMetadata } from "@moment/api-collab/api-types";
import { LoadingSkeleton } from "@moment/design-system/LoadingSkeleton";

import { Editor } from "~/components/canvas/editor/BigRichTextEditor";
import { EditorHeader } from "~/components/canvas/editor/EditorHeader";
import { usePrintArea } from "~/components/common/print-area/usePrintArea";
import { usePageData } from "~/state";
import { useSelectAllCellsReady } from "~/state/machines/cells";
import { cn } from "~/ui/utils";

import { useEasel } from "../../easel";
import { useEaselCurrentPage } from "../../hooks/useEaselCurrentPage";
import { Row } from "../../layout/row/Row";
import { StaticDocumentHead } from "../StaticDocumentHead";
import { CANVAS_BODY_ID } from "../constants";
import { Byline } from "./Byline";
import { DocumentCreatedDate } from "./DocumentCreatedDate";
import { Header } from "./Header";

export type CanvasProps = {
	canvas: DocumentMetadata;
};

/**
 * <CanvasView/>
 * This component is essentially the canvas content
 *
 * TODO:
 * Refactor this component so that it uses "runtime" and "canvas" from "useEasel"
 * instead of receiving them as props
 */
export const PublishedDocument: FC<CanvasProps> = ({ canvas }) => {
	// Hooks
	const allCellsReady = useSelectAllCellsReady();
	const [isFirstPageLoad, setIsFirstPageLoad] = useState(true);
	const { printAreaContentRef } = usePrintArea();

	const { workspaceName, documentId } = useEasel();
	const activePage = useEaselCurrentPage();
	const pageData = usePageData(documentId, activePage?.id ?? "");

	const showSkeleton = useMemo(() => {
		if (isFirstPageLoad && allCellsReady) {
			/* if we are still on first page load and all cells are initialized then turn off "firstPageLoad" and return false */
			setIsFirstPageLoad(false);
		} else if (isFirstPageLoad && !allCellsReady) {
			/* if we are on first page load and cells are still loading */
			return true;
		}

		return false;
	}, [allCellsReady, isFirstPageLoad]);

	// Refs
	const editorContainerRef = useRef<HTMLDivElement>(null);

	const canvasWritable = false;

	return (
		<>
			{/* ------ Client Meta Tags ------ */}
			<StaticDocumentHead canvas={canvas} pageData={pageData} pageSlug={activePage?.slug} />

			{/* ------ TOP BAR ------ */}
			<div className="@container/main relative rich-text bg-primary border-b border-dashed">
				<Row>
					<Header workspaceName={workspaceName} documentMetadata={canvas} />
				</Row>
			</div>
			<main className="bg-primary @container/main relative flex flex-1 flex-col focus:outline-none">
				{/* --- CANVAS --- */}
				<div className="relative flex flex-auto" ref={printAreaContentRef}>
					<div
						className={cn(
							"absolute",
							"print:static",
							"flex",
							"print:block",
							"inset-0",
							"overflow-y-auto overflow-x-hidden",
							"print:overflow-visible",
							"print:bg-primary"
						)}
						style={{ transform: "translateZ(0)" }}
					>
						{/* --- PAGES PANE --- */}
						<div className="flex grow flex-col overflow-y-auto">
							<div
								id={CANVAS_BODY_ID}
								className={cn(
									"relative",
									"print:static",
									"flex flex-auto flex-col",
									"print:block",
									"overflow-y-auto",
									"print:overflow-visible",
									"scroll-smooth"
								)}
							>
								<DocumentCreatedDate documentMetadata={canvas} />

								<EditorHeader writable={canvasWritable} />

								<Byline activePageMetadata={activePage} />

								<div className="min-h-[1rem]" />

								{/* --- DOCUMENT STATE --- */}
								{match({
									showSkeleton,
								})
									.with({ showSkeleton: true }, () => (
										<LoadingSkeleton
											barCount={10}
											randomizeWidths
											showChildren={!showSkeleton}
											SkeletonWrapper={({
												children,
											}: FC["arguments"]["children"]) => (
												<div className="rich-text">
													<div className="canvas-content-row space-y-4">
														{children}
													</div>
												</div>
											)}
										/>
									))
									.with({ showSkeleton: false }, () => (
										<Editor ref={editorContainerRef} />
									))
									.exhaustive()}
							</div>
						</div>
					</div>
				</div>
			</main>
		</>
	);
};
